import React, { useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import { getProductReportAPI, getCategoriesAPI } from "../../../utils/api";
import "./MetaProductReport.css";
import { BASE_URL, DOMAIN } from "../../../utils/configConstants";

const ProductReport = ({ showToast }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reportFilters, setReportFilters] = useState({
    fromDate: "",
    toDate: "",
    category: "",
    status: ""
  });

  const handleReportFilterChange = (field, value) => {
    setReportFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategoriesAPI({});
      if (response?.status === 200) {
        setCategories(response?.data || []);
      }
    } catch (err) {
      console.log('Error fetching categories:', err);
    }
  };

  const generateTextFile = async () => {
    try {
      setLoading(true);

      let requestBody = {};
      if (reportFilters.fromDate) {
        const d = new Date(reportFilters.fromDate);
        requestBody.from_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (reportFilters.toDate) {
        const d = new Date(reportFilters.toDate);
        requestBody.to_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (reportFilters.category && reportFilters.category !== "all") {
        requestBody.category_id = reportFilters.category;
      }
      if (reportFilters.status && reportFilters.status !== "all") {
        requestBody.status = reportFilters.status;
      }

      const response = await getProductReportAPI(requestBody);
      if (response?.status !== 200) {
        showToast('Failed to fetch data for download', 'error');
        return;
      }

      const reportProducts = response?.data || [];
      if (reportProducts.length === 0) {
        showToast('No data available to export', 'error');
        return;
      }

      let textContent = "";
      // Add header row
      textContent += "id\ttitle\tdescription\tprice\tcondition\tlink\tavailability\timage_link\tbrand\tgoogle_product_category\tfb_product_category\tsale_price\tmaterial\n";
      
      reportProducts.forEach((product) => {
        const id = product?.sku || '';
        const title = product?.name || '';
        const description = product?.description ? product?.description.replace(/<[^>]*>/g, '').substring(0, 9999) : title;
        const price = product?.size?.retail_price ? `${product?.size?.retail_price} INR` : '';
        const condition = 'new';
        const link = `${DOMAIN}product?product_id=${product?._id || ''}`;
        const availability = 'in stock';
        const imageLink = product?.image?.image ? `${BASE_URL}${product?.image?.image}` : '';
        const brand = product?.brand?.name || '';
        const googleProductCategory ='Apparel & Accessories > Clothing';
        const fbProductCategory = 'Clothing & Accessories > Clothing';
        const salePrice = product?.size?.mrp ? `${product?.size?.mrp} INR` : '';
        const material = product?.fabric || '';
        const color = product?.colors && product?.colors.length > 0 ? product?.colors[0]?.name : '';
        const size = product?.size?.size || '';

        textContent += `${id}\t${title}\t${description}\t${price}\t${condition}\t${link}\t${availability}\t${imageLink}\t${brand}\t${googleProductCategory}\t${fbProductCategory}\t${salePrice}\t${material}\n${color}\n${size}\n${size}`;
      });

      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `product-report-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showToast('Text file exported successfully', 'success');
    } catch (err) {
      console.log('Error exporting text file:', err);
      showToast('Failed to export text file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearReportFilters = () => {
    setReportFilters({
      fromDate: "",
      toDate: "",
      category: "",
      status: ""
    });
    fetchReportData({
      fromDate: "",
      toDate: "",
      category: "",
      status: ""
    });
  };

  const fetchReportData = async (filters) => {
    try {
      setLoading(true);
      setError(null);

      let requestBody = {};
      if (filters.fromDate) {
        const date = new Date(filters.fromDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.from_date = `${day}-${month}-${year}`;
      }
      if (filters.toDate) {
        const date = new Date(filters.toDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.to_date = `${day}-${month}-${year}`;
      }
      if (filters.category && filters.category !== "all") {
        requestBody.category_id = filters.category;
      }
      if (filters.status && filters.status !== "all") {
        requestBody.status = filters.status;
      }

      const response = await getProductReportAPI(requestBody);

      if (response?.status === 200) {
        setProducts(response?.data || []);
      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
      } else {
        setError('Failed due to internal error');
      }
    } catch (err) {
      console.log('Error fetching report data:', err);
      setError('Failed due to internal error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    fetchReportData(reportFilters);
  };

  useEffect(() => {
    fetchCategories();
    fetchReportData({
      fromDate: "",
      toDate: "",
      category: "",
      status: ""
    });
  }, []);

  return (
    <div className="product-report">
      <div className="report-filter-row">
        <div className="report-filter-group">
          <label>From Date</label>
          <input
            type="date"
            value={reportFilters.fromDate}
            onChange={(e) => handleReportFilterChange('fromDate', e.target.value)}
            className="report-filter-input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="report-filter-group">
          <label>To Date</label>
          <input
            type="date"
            value={reportFilters.toDate}
            onChange={(e) => handleReportFilterChange('toDate', e.target.value)}
            className="report-filter-input"
            max={new Date().toISOString().split('T')[0]}
            min={reportFilters.fromDate}
          />
        </div>
        <div className="report-filter-group">
          <label>Category</label>
          <select
            value={reportFilters.category}
            onChange={(e) => handleReportFilterChange('category', e.target.value)}
            className="report-filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div className="report-filter-group">
          <label>Status</label>
          <select
            value={reportFilters.status}
            onChange={(e) => handleReportFilterChange('status', e.target.value)}
            className="report-filter-select"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div className="report-filter-actions">
        <button className="report-clear-btn" onClick={clearReportFilters}>
          Clear
        </button>
        <button className="report-generate-btn" onClick={onSubmit}>
          Submit
        </button>
        <button className="report-generate-btn" onClick={generateTextFile}>
          <FaFileExcel />
          Download
        </button>
      </div>

      {loading && (
        <div className="report-loading-container">
          <div className="report-loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      )}

      {error && (
        <div className="report-error-container">
          <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
          <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>

          {products.length > 0 && (
            <div className="report-summary" style={{
              marginTop:'25px'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr className="meta-produt-report-table-header">
                      <th>id</th>
                      <th>title</th>
                      <th>description</th>
                      <th>price</th>
                      <th>condition</th>
                      <th>link</th>
                      <th>availability</th>
                      <th>image_link</th>
                      <th>brand</th>
                      <th>google_product_category</th>
                      <th>fb_product_category</th>
                      <th>sale_price</th>
                      <th>material</th>
                      <th>color</th>
                      <th>size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr key={product._id || index} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td className="meta-produt-report-table-cell">{product?.sku || ''}</td>
                        <td className="meta-produt-report-table-cell-title">{product?.name || ''}</td>
                        <td className="meta-produt-report-table-cell-description">
                          {product?.description ? product?.description.replace(/<[^>]*>/g, '').substring(0, 9999) : ''}
                        </td>
                        <td className="meta-produt-report-table-cell">
                          {product?.size?.retail_price ? `${product?.size?.retail_price} INR` : ''}
                        </td>
                        <td className="meta-produt-report-table-cell">new</td>
                        <td className="meta-produt-report-table-cell">
                          <a
                            href={`${DOMAIN}product?product_id=${product?._id || ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#0A727E', textDecoration: 'none' }}
                          >
                           {`${DOMAIN}product?product_id=${product?._id || ''}`}
                          </a>
                        </td>
                        <td className="meta-produt-report-table-cell">in stock</td>
                        <td className="meta-produt-report-table-cell-image">
                          {product?.image?.image ? (
                            <img
                              src={`${BASE_URL}${product?.image?.image}`}
                              alt={product?.name || 'Product image'}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <span style={{ display: product?.image?.image ? 'none' : 'block', color: '#9ca3af', fontSize: '0.75rem' }}>
                            No image
                          </span>
                        </td>
                        <td className="meta-produt-report-table-cell">{product?.brand?.name || ''}</td>
                         <td className="meta-produt-report-table-cell">{'Apparel & Accessories > Clothing'}</td>
                         <td className="meta-produt-report-table-cell">{'Clothing & Accessories > Clothing'}</td>
                          <td className="meta-produt-report-table-cell">
                          {product?.size?.mrp ? `${product?.size?.mrp} INR` : ''}
                        </td>
                          <td className="meta-produt-report-table-cell">
                          {product?.fabric ? `${product?.fabric}` : ''}
                        </td>
                            <td className="meta-produt-report-table-cell">
                            {product?.colors && product?.colors.length > 0 ? product?.colors[0]?.name : ''}
                        </td>
                        <td className="meta-produt-report-table-cell">
                        {product?.size?.size || ''}
                    </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductReport;
